import { useRef, useState } from 'react';

import { FileUp, LoaderCircle } from 'lucide-react';

import { UPLOAD_ACCEPT, UPLOAD_MAX_MB } from '../constants';

import styles from '../index.module.scss';

export default function UploadZone({ uploading, disabled, onUpload }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const interactive = !uploading && !disabled;

    const pick = (file) => {
        if (file && interactive) onUpload(file);
    };

    const openPicker = () => {
        if (interactive) inputRef.current?.click();
    };

    const clickable = interactive
        ? {
              role: 'button',
              tabIndex: 0,
              'aria-label': '点击选择文档上传，或把文件拖到这里',
              'aria-describedby': 'ppt-upload-hint',
              onClick: openPicker,
              onKeyDown: (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openPicker();
                  }
              },
          }
        : {};

    return (
        <div
            className={styles.dropzone}
            data-dragging={dragging}
            {...clickable}
            onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                pick(event.dataTransfer.files?.[0]);
            }}
        >
            {uploading ? (
                <div className={styles.dropzoneBusy} role="status">
                    <LoaderCircle className={styles.spin} size={22} aria-hidden />
                    <p>
                        {uploading.percent < 100
                            ? `正在上传「${uploading.name}」… ${uploading.percent}%`
                            : `正在解析「${uploading.name}」的正文…`}
                    </p>
                </div>
            ) : (
                <>
                    <span className={styles.dropIcon} aria-hidden>
                        <FileUp size={26} />
                    </span>
                    <p className={styles.dropTitle}>
                        拖拽论文到这里，或
                        <span className={styles.dropLink}>点击选择文件</span>
                    </p>
                    <p className={styles.dropHint} id="ppt-upload-hint">
                        支持 Word（.docx / .doc）、PDF、TXT，最大 {UPLOAD_MAX_MB}MB。
                        会先整理出章节要点和原文证据，扫描件和图片中的文字无法识别。
                    </p>
                </>
            )}
            <input
                ref={inputRef}
                type="file"
                accept={UPLOAD_ACCEPT}
                className={styles.srOnly}
                tabIndex={-1}
                aria-hidden
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => {
                    pick(event.target.files?.[0]);
                    event.target.value = '';
                }}
            />
        </div>
    );
}
